/**
 * Reaching the terminal server on THIS machine (RFC 0276 §3, §5).
 *
 * DISCOVERY IS THE PROBE. There is no `terminals` field on the presence beat
 * and this file deliberately does not read the World: a shell on the machine
 * under your hands is not a fact that has to travel, and routing it through a
 * synced document would mean a relay outage takes away your own terminal. The
 * precedent is `bridge/capabilities/vcs.ts`, which fetches a localhost git
 * server that nothing advertises; availability there is "did the fetch answer",
 * and it is the same question here.
 *
 * WHY http:// FROM AN https:// PAGE IS FINE. The desktop shell loads the
 * deployed app over https on purpose (one project UI, never a bundled fork),
 * so this is a cross-origin call to loopback. It is not mixed content:
 * `http://localhost` is a potentially-trustworthy origin, which is why the vcs
 * endpoint has worked from the same page since RFC 0285. The server answers the
 * private-network preflight for the same reason.
 *
 * The transport interface is the seam RFC 0276 §5 asks for. A relayed transport
 * for a machine you are NOT on (S4) implements the same three methods, and the
 * view does not learn which one it got.
 */

import { projectEndpoint } from '../../sdk/localServer'

/** Where the daemon serves a project's terminals: a path on its one server
 *  (RFC 0331 3a), so a shell opens in that project's checkout. */
const termEndpoint = (projectId: string) => projectEndpoint(projectId, 'term')

/** What this machine is, and which shells are alive on it. */
export interface LocalTerminals {
  deviceId: string
  shell: string
  cwd: string
  live: { id: string; title: string }[]
}

/**
 * Ask this machine whether it can give you a shell.
 *
 * Null when nothing is serving — no daemon, no pty support, a browser rather
 * than the desktop app. Null is an ordinary answer, not an error: the ways in
 * are simply not offered, and nothing else in the app has to know why.
 */
export async function probeLocalTerminals(
  projectId: string,
  signal?: AbortSignal,
): Promise<LocalTerminals | null> {
  try {
    const res = await fetch(`${termEndpoint(projectId)}/probe`, {
      ...(signal ? { signal } : {}),
    })
    if (!res.ok) return null
    const body = (await res.json()) as Partial<LocalTerminals>
    if (typeof body.deviceId !== 'string' || !body.deviceId) return null
    return {
      deviceId: body.deviceId,
      shell: typeof body.shell === 'string' ? body.shell : 'shell',
      cwd: typeof body.cwd === 'string' ? body.cwd : '',
      live: Array.isArray(body.live)
        ? body.live.filter(
            (s): s is { id: string; title: string } =>
              !!s && typeof s.id === 'string' && typeof s.title === 'string',
          )
        : [],
    }
  } catch {
    // No server, a refused connection, a page that is not in the desktop app.
    return null
  }
}

/**
 * Start a shell, and get back the id the server minted for it.
 *
 * SEPARATE FROM ATTACHING, deliberately. The obvious shortcut is to open a
 * socket with no id and let the server make one — and it is a trap: it makes
 * creating a process a side effect of a VIEW being mounted. React's strict mode
 * mounts every effect twice in development, so that shortcut spawned two shells
 * per click and left one of them running with nothing pointed at it. Minting
 * first means every socket the app opens names a shell that already exists, and
 * a remount can only ever re-attach.
 */
export async function createTerminal(
  projectId: string,
  size: TerminalSize,
): Promise<{ id: string; title: string }> {
  const res = await fetch(`${termEndpoint(projectId)}/new`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(size),
  })
  const body = (await res.json()) as {
    id?: string
    title?: string
    error?: string
  }
  // The server's own words when it has them: "at its limit of 10 shells" is
  // something you can act on, and a generic failure is not.
  if (!res.ok || !body.id)
    throw new Error(body.error ?? 'could not start a shell on this machine')
  return { id: body.id, title: body.title ?? 'Terminal' }
}

/** End a shell deliberately. The one call that stops a process on purpose. */
export async function endTerminal(
  projectId: string,
  id: string,
): Promise<boolean> {
  try {
    const res = await fetch(`${termEndpoint(projectId)}/end`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id }),
    })
    return res.ok
  } catch {
    return false
  }
}

export interface TerminalSize {
  cols: number
  rows: number
}

/** The one interface the view sees. Locality picks the implementation. */
export interface TerminalTransport {
  /** Resolves with the shell's id — which is minted by the SERVER, so a block
   *  that asked for "a terminal" learns which one it got and can put it in the
   *  url. */
  open(): Promise<{ id: string; title: string }>
  write(data: string): void
  resize(size: TerminalSize): void
  onData(fn: (chunk: string) => void): () => void
  onExit(fn: (code: number) => void): () => void
  close(): void
}

type ServerMessage =
  | { t: 'ready'; id: string; title: string; cols: number; rows: number }
  | { t: 'data'; d: string }
  | { t: 'exit'; code: number }
  | { t: 'error'; message: string }

/** http://host -> ws://host, so one endpoint configures both channels. */
function wsEndpoint(projectId: string): string {
  return termEndpoint(projectId).replace(/^http/, 'ws')
}

/**
 * A shell on this machine, over a WebSocket.
 *
 * ALWAYS BY ID. Attaching is only ever "give me that shell back" — it cannot
 * create one, which is what `createTerminal` is for. Re-attaching after a pane
 * was closed an hour ago and after a page reload a second ago are then the same
 * request, and nothing here can tell them apart, which is the point.
 *
 * (The server still accepts an attach with no id, for `term-serve` used by hand
 * and by the tests. The app never does it: see createTerminal.)
 */
export function localTerminalTransport(
  projectId: string,
  terminalId: string,
  size: TerminalSize,
): TerminalTransport {
  const data = new Set<(chunk: string) => void>()
  const exits = new Set<(code: number) => void>()
  let socket: WebSocket | null = null
  // Input typed before the socket opens is not thrown away: a person who
  // starts typing the moment a pane appears expects their keystrokes to land.
  let pending: string[] = []
  let closed = false

  const send = (msg: unknown): void => {
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify(msg))
      return
    }
    if (typeof msg === 'object' && msg && (msg as { t?: string }).t === 'input')
      pending.push((msg as { d: string }).d)
  }

  return {
    open: () =>
      new Promise((resolve, reject) => {
        const q = new URLSearchParams({
          cols: String(size.cols),
          rows: String(size.rows),
        })
        q.set('id', terminalId)
        const ws = new WebSocket(
          `${wsEndpoint(projectId)}/attach?${q.toString()}`,
        )
        socket = ws
        let settled = false
        ws.onmessage = (event) => {
          let msg: ServerMessage
          try {
            msg = JSON.parse(String(event.data)) as ServerMessage
          } catch {
            return
          }
          if (msg.t === 'ready') {
            settled = true
            // Flush what was typed while connecting, in order.
            for (const d of pending) ws.send(JSON.stringify({ t: 'input', d }))
            pending = []
            resolve({ id: msg.id, title: msg.title })
          } else if (msg.t === 'data') {
            for (const fn of data) fn(msg.d)
          } else if (msg.t === 'exit') {
            for (const fn of exits) fn(msg.code)
          } else if (msg.t === 'error' && !settled) {
            settled = true
            reject(new Error(msg.message))
          }
        }
        ws.onerror = () => {
          if (!settled) {
            settled = true
            reject(new Error('no terminal server on this machine'))
          }
        }
        ws.onclose = () => {
          if (!settled) {
            settled = true
            reject(new Error('the terminal server closed the connection'))
          }
          // A socket that drops with the shell still alive is a disconnection,
          // not an exit — the process is on the machine and outlived it. The
          // view re-attaches rather than reporting a shell that ended.
        }
      }),

    write: (d) => send({ t: 'input', d }),

    resize: ({ cols, rows }) => {
      if (cols > 0 && rows > 0) send({ t: 'resize', cols, rows })
    },

    onData: (fn) => {
      data.add(fn)
      return () => data.delete(fn)
    },

    onExit: (fn) => {
      exits.add(fn)
      return () => exits.delete(fn)
    },

    close: () => {
      if (closed) return
      closed = true
      // CLOSING THE SOCKET IS A DETACH. The server keeps the pty, which is the
      // whole lifetime rule: closing a pane closes a view (RFC 0276 §4).
      socket?.close()
      socket = null
      data.clear()
      exits.clear()
    },
  }
}
